import { ChangeEvent, useCallback, useImperativeHandle, useState } from 'react';

import {
  IImageCropperPresenterParams,
  IImageCropperPresenter,
  ICroppedAreaPixels,
  ICrop,
} from './ImageCropper.types';
import { createPreCroppedImage } from './ImageCropper.utils';

export function useImageCropperPresenter(
  params: IImageCropperPresenterParams
): IImageCropperPresenter {
  const { image, onError, ref } = params;

  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<ICroppedAreaPixels>({});
  const [isLoading, setIsLoading] = useState(false);

  const getCroppedImg = useCallback((): Promise<string> => {
    const preCroppedImage = createPreCroppedImage(image);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const maxDimension = croppedAreaPixels.width || croppedAreaPixels.height;

    canvas.width = maxDimension!;
    canvas.height = maxDimension!;

    ctx!.drawImage(
      preCroppedImage,
      croppedAreaPixels.x!,
      croppedAreaPixels.y!,
      maxDimension!,
      maxDimension!,
      0,
      0,
      maxDimension!,
      maxDimension!
    );

    return new Promise((resolve) => {
      canvas.toBlob((blob) => {
        resolve(window.URL.createObjectURL(blob!));
      }, 'image/png');
    });
  }, [createPreCroppedImage, croppedAreaPixels, image]);

  const handleCropImage = useCallback(async (): Promise<string> => {
    setIsLoading(true);
    const base64Image = await getCroppedImg();
    setIsLoading(false);
    return base64Image;
  }, [getCroppedImg]);

  const onZoomChange = useCallback(
    (zoom: number) => {
      if (isLoading) {
        return;
      }

      setZoom(zoom);
    },
    [isLoading]
  );

  const onZoomChangeSlider = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    if (isLoading) {
      return;
    }

    setZoom(parseFloat(event.target.value));
  }, []);

  const onCropComplete = useCallback(
    (_: unknown, croppedAreaPixels: ICroppedAreaPixels) => {
      if (isLoading) {
        return;
      }

      setCroppedAreaPixels(croppedAreaPixels);
    },
    [isLoading]
  );

  const onCropChange = useCallback(
    (crop: ICrop) => {
      if (isLoading) {
        return;
      }

      setCrop(crop);
    },
    [isLoading]
  );

  const onCropperError = useCallback(() => {
    onError?.();
  }, []);

  useImperativeHandle(ref, () => ({
    getCroppedImage: (): Promise<string> => {
      return handleCropImage();
    },
  }));

  return {
    crop,
    zoom,
    isLoading,
    onCropperError,
    onCropChange,
    onCropComplete,
    onZoomChangeSlider,
    onZoomChange,
    handleCropImage,
  };
}
